import { InnerBox } from "@/components/elements/InnerBox";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { Tooltip } from "@/components/elements/Tooltip";
import { IconErrorOutline } from "@/assets/icons";
import { black, text } from "@/theme/colors";
import { DebounceInput } from "@/components/elements/Input/Input";
import { Select } from "@/components/elements/Select";
import { useGetCompanySpecificIndustryList, useGetNationalityList } from "@/api/services/global/master";
import { Button } from "@/components/elements/Button";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { useGetPartnerList } from "@/api/services/main/partner";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useTalentList } from "@/api/services/main/talent";
import { usePagination } from "@/hooks/usePagination";
import { useSearchParams } from "react-router-dom";
import { TGetTalentListReqDto } from "@/api/services/main/talent/dtos/get-talent-list.dto";
import { START_PAGE } from "@/constants/pagination";
import Divider from "@mui/material/Divider";
import { Pagination } from "@/components/elements/Pagination";
import { FCC } from "@/types/common";
import { ERecruitmentStatus } from "@/types/enum";
import { TFilter } from "@/api/services/main/case/dtos/create-case.dto";
import { CaseMatchTable } from "@/features/case/components/CaseMatches/CaseMatchTable";

type TCaseMatchesProps = {
  setIsSelectAll: Dispatch<SetStateAction<boolean>>;
};

const defaultValues: TFilter = {
  keyword: "",
  companySpecificIndustryId: undefined,
  nationalityId: undefined,
  personInChargeId: undefined,
  partnerId: undefined,
};

export const CaseMatches: FCC<TCaseMatchesProps> = ({ setIsSelectAll }) => {
  const [filter, setFilter] = useState<TFilter>({});
  const [formValues, setFormValues] = useState<Partial<TFilter>>({});
  const pagReq = usePagination();

  const { nationalityOptions } = useGetNationalityList();
  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const { userOptions } = useGetUserListPersonInCharge();
  const { partnerOptions } = useGetPartnerList();
  const [searchParams, setSearchParams] = useSearchParams();

  const query: TGetTalentListReqDto = {
    page: pagReq.page || START_PAGE,
    perPage: 10,
    recruitmentStatus: ERecruitmentStatus.JOB_SEEKING,
    isCase: 1,
    ...formValues,
  };

  if (searchParams.get("sorts")) {
    query.sorts = searchParams.get("sorts") ? [searchParams.get("sorts") as `${string}|${string}`] : [];
  }

  const { data: talentResponse } = useTalentList(query);

  const navigateFirstPage = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.set("page", "1");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const onSearch = useCallback(
    (value: TFilter) => {
      setFormValues(value);
      navigateFirstPage();
    },
    [navigateFirstPage],
  );

  const clearFilter = () => {
    setFilter({ ...defaultValues });
    onSearch(defaultValues);
  };

  const handleChange = (key: keyof TFilter, value: number | string | undefined) => {
    setFilter((prev) => {
      const newFilter = { ...prev };
      if (value === undefined || value === null || value === "" || value === 0) {
        delete newFilter[key];
      } else {
        if (key === "keyword") {
          newFilter[key] = value as string;
        } else {
          newFilter[key] = value as number;
        }
      }
      return newFilter;
    });
  };

  useEffect(() => {
    const isFilterEmpty =
      !filter.keyword && !filter.companySpecificIndustryId && !filter.nationalityId && !filter.personInChargeId && !filter.partnerId;

    const isFormValuesEmpty =
      !formValues.keyword &&
      !formValues.companySpecificIndustryId &&
      !formValues.nationalityId &&
      !formValues.personInChargeId &&
      !formValues.partnerId;

    if (isFilterEmpty && !isFormValuesEmpty) {
      onSearch(defaultValues);
    }
  }, [filter, formValues, onSearch]);

  const isSubmitEnabled = useMemo(() => {
    return Boolean(filter.keyword || filter.companySpecificIndustryId || filter.nationalityId || filter.personInChargeId || filter.partnerId);
  }, [filter]);

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  const page = talentResponse?.pagination?.page ?? 0;
  const perPage = talentResponse?.pagination?.perPage ?? 0;
  const count = talentResponse?.pagination?.count ?? 10;

  const countItem = count + perPage * page - perPage;

  return (
    <>
      <InnerBox mt={3} showDivider={false} title={<Typography variant="head20Bold">人材の選択</Typography>}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="cap12Light">キーワード検索</Typography>
                <Tooltip title="以下から検索ができます。" content="名前、呼び名、パートナー名、職歴、学歴、資格、日本語能力">
                  <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle", lineHeight: 0 }}>
                    <IconErrorOutline width={15} height={15} color={black[80]} />
                  </Box>
                </Tooltip>
              </Stack>
              <DebounceInput
                onChange={(e) => handleChange("keyword", e.target.value || undefined)}
                placeholder="入力してください"
                value={filter.keyword || ""}
                size="md"
                style={{ width: "100%" }}
              />
            </Stack>
          </Grid>
          <Grid item xs={4}>
            <Stack spacing={1}>
              <Typography variant="cap12Light">分野</Typography>
              <Select
                placeholder="選択してください"
                value={filter.companySpecificIndustryId}
                options={companySpecificIndustryOptions}
                onChange={(value) => handleChange("companySpecificIndustryId", value || undefined)}
                size="md"
                style={{ width: "100%" }}
              />
            </Stack>
          </Grid>
          <Grid item xs={4}>
            <Stack spacing={1}>
              <Typography variant="cap12Light">国籍</Typography>

              <Select
                placeholder="選択してください"
                value={filter.nationalityId}
                options={nationalityOptions}
                onChange={(value) => handleChange("nationalityId", value || undefined)}
                size="md"
                style={{ width: "100%" }}
              />
            </Stack>
          </Grid>
          <Grid item xs={4}>
            <Stack spacing={1}>
              <Typography variant="cap12Light">パートナー</Typography>
              <Select
                placeholder="選択してください"
                value={filter.partnerId}
                onChange={(value) => handleChange("partnerId", value || undefined)}
                options={partnerOptions}
                size="md"
                style={{ width: "100%" }}
              />
            </Stack>
          </Grid>
          <Grid item xs={4}>
            <Stack spacing={1}>
              <Typography variant="cap12Light">人材担当者</Typography>
              <Select
                placeholder="選択してください"
                value={filter.personInChargeId}
                onChange={(value) => handleChange("personInChargeId", value || undefined)}
                options={userOptions}
                size="md"
                style={{ width: "100%" }}
              />
            </Stack>
          </Grid>
          <Grid item xs={4}>
            <Stack direction="row" alignItems="end" alignContent="end" spacing={1} mt={3}>
              <Button type="button" variant="text">
                <Typography variant="body14Semi" onClick={clearFilter}>
                  クリア
                </Typography>
              </Button>
              <Button type="button" variant="outline" onClick={() => onSearch(filter)} disabled={!isSubmitEnabled}>
                <Typography variant="body14Semi">検索</Typography>
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Divider
              sx={{
                marginTop: "8px",
                marginBottom: "24px",
              }}
            />
            <Typography variant="sub16Bold" mt={3}>
              人材一覧
            </Typography>
            <Stack direction="row" mt={2} mb={2}>
              <Typography variant="body14Semi" color={text.primary}>
                全 {formatTotal(talentResponse?.pagination?.total) ?? 0} 件中 {countItem ?? 0} 件を表示
              </Typography>
            </Stack>

            <CaseMatchTable rows={talentResponse?.data ?? []} page={page} setIsSelectAll={setIsSelectAll} />

            <Stack mt={3}>
              <Pagination totalPage={talentResponse?.pagination?.totalPage ?? 1} />
            </Stack>
          </Grid>
        </Grid>
      </InnerBox>
    </>
  );
};
